import React, { useState, useRef, useEffect } from 'react';
import './attestation.css';
import app from './firebase.js';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import html2pdf from 'html2pdf.js';
import { Delete, Download, Eye, Globe } from 'react-feather';
import { Modal, Button, ConfigProvider } from 'antd';
import QRCode from 'qrcode';
import logo from "./img/edaplogo.png"

const Attestation = () => {
    const [formData, setFormData] = useState({
        language: 'Français',
        genre: 'Femme',
        noms: '',
        matricule: '',
        filiere: '',
        departement: '',
        classe: '',
        anneeAcademique: '',
        laboratoire: '',
        universiteCodeplomation: '',
        dateEmission: new Date().toISOString().split('T')[0]
    });

    const [attestations, setAttestations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date'); // 'date' or 'name'
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedAttestation, setSelectedAttestation] = useState(null);
    const [displayLanguage, setDisplayLanguage] = useState('Français'); // Language for viewing existing documents
    const attestationRef = useRef();
    const db = getDatabase(app);

    // Load attestations from Firebase on component mount
    useEffect(() => {
        const attestationsRef = ref(db, 'Inscription/doctorant/UNCR/');
        onValue(attestationsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const attestationArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                // Sort by creation date, newest first
                attestationArray.sort((a, b) => {
                    const dateA = new Date(a.createdAt || 0);
                    const dateB = new Date(b.createdAt || 0);
                    return dateB - dateA;
                });
                setAttestations(attestationArray);
            }
        });
    }, [db]);

    // Handle ESC key press to close modal
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && modalVisible) {
                handleModalClose();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [modalVisible]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.noms.trim() === '' || formData.matricule.trim() === '') {
            alert('Nom et Matricule sont obligatoires!');
            return;
        }

        // Save to Firebase
        const attestationRef = ref(db, `Inscription/doctorant/UNCR/${formData.matricule}`);
        set(attestationRef, {
            ...formData,
            createdAt: new Date().toISOString()
        })
            .then(() => {
                console.log('Attestation saved successfully in Firebase');
            })
            .catch((error) => {
                console.error('Error saving attestation to Firebase: ', error);
            });

        // Reset form
        setFormData({
            language: formData.language, // Keep language selection
            genre: 'Femme',
            noms: '',
            matricule: '',
            filiere: '',
            departement: '',
            classe: '',
            anneeAcademique: '',
            laboratoire: '',
            universiteCodeplomation: '',
            dateEmission: new Date().toISOString().split('T')[0]
        });
    };

    const showAttestationModal = (attestation) => {
        setSelectedAttestation(attestation);
        // Set display language to the document's original language, or default to French
        setDisplayLanguage(attestation.language || 'Français');
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedAttestation(null);
        setDisplayLanguage('Français'); // Reset to default
    };

    const toggleDisplayLanguage = () => {
        setDisplayLanguage(prev => prev === 'Français' ? 'English' : 'Français');
    };

    const handleDeleteAttestation = (id) => {
        const attestationRef = ref(db, `Inscription/doctorant/UNCR/${id}`);
        remove(attestationRef)
            .then(() => {
                console.log('Attestation deleted successfully from Firebase');
            })
            .catch((error) => {
                console.error('Error deleting attestation from Firebase: ', error);
            });
    };

    const downloadPDF = (attestation) => {
        const element = document.getElementById(`attestation-${attestation.matricule}`);
        
        // Create a clone of the element with inline styles for PDF generation
        const clone = element.cloneNode(true);
        clone.style.cssText = `
            width: 19cm !important;
            min-height: 20.7cm !important;
            margin: 0 auto !important;
            padding: 2cm !important;
            background-color: white !important;
            font-family: 'Times New Roman', serif !important;
            line-height: 1.4 !important;
            position: relative !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            page-break-inside: avoid !important;
        `;
        
        // Apply inline styles to critical elements
        const applyInlineStyles = (elem) => {
            // Header styles
            const header = elem.querySelector('.attestation-header');
            if (header) {
                header.style.cssText = 'text-align: center !important; margin-bottom: 30px !important;';
            }
            
            // Academy name
            const academyName = elem.querySelector('.academy-name');
            if (academyName) {
                academyName.style.cssText = 'color: #28a745 !important; font-size: 20px !important; font-weight: bold !important; margin: 0 0 10px 0 !important;';
            }
            
            // University name
            const universityName = elem.querySelector('.university-name');
            if (universityName) {
                universityName.style.cssText = 'color: #28a745 !important; font-size: 18px !important; font-weight: bold !important; margin: 10px 0 !important;';
            }
            
            // Address and contact
            const address = elem.querySelector('.address');
            const contact = elem.querySelector('.contact');
            if (address) address.style.cssText = 'font-size: 12.5px !important; color: #333 !important; margin: 5px 0 !important;';
            if (contact) contact.style.cssText = 'font-size: 12.5px !important; color: #333 !important; margin: 5px 0 !important;';
            
            // Separator
            const separator = elem.querySelector('.separator');
            if (separator) {
                separator.style.cssText = 'height: 2px !important; background-color: #000 !important; margin: 20px 0 !important; border: none !important;';
            }
            
            // Title
            const title = elem.querySelector('.title');
            if (title) {
                title.style.cssText = 'text-align: center !important; font-size: 18px !important; font-weight: bold !important; color: #000 !important; margin: 10px 0 !important; text-transform: uppercase !important;';
            }
            
            // Academic year
            const academicYear = elem.querySelector('.academic-year');
            if (academicYear) {
                academicYear.style.cssText = 'text-align: center !important; font-size: 16px !important; font-weight: bold !important;';
            }
            
            // Document body
            const documentBody = elem.querySelector('.document-body');
            if (documentBody) {
                documentBody.style.cssText = 'font-size: 12.5px !important; line-height: 1.8 !important;';
            }
            
            // Student details
            const studentDetails = elem.querySelector('.student-details');
            if (studentDetails) {
                studentDetails.style.cssText = 'margin: 20px 0 !important;';
                const detailsParagraphs = studentDetails.querySelectorAll('p');
                detailsParagraphs.forEach(p => {
                    p.style.cssText = 'margin: 8px 0 !important; font-size: 12.5px !important;';
                });
            }
            
            // Registration text
            const registrationText = elem.querySelector('.registration-text');
            if (registrationText) {
                registrationText.style.cssText = 'margin: 20px 0 !important; text-align: justify !important; font-size: 12.5px !important;';
            }
            
            // Introduction
            const introduction = elem.querySelector('.introduction');
            if (introduction) {
                introduction.style.cssText = 'margin-bottom: 20px !important; text-align: justify !important; font-size: 12.5px !important;';
            }
            
            // Signature section
            const signatureSection = elem.querySelector('.signature-section');
            if (signatureSection) {
                signatureSection.style.cssText = 'margin-top: 40px !important;';
            }
            
            const dateLocation = elem.querySelector('.date-location');
            if (dateLocation) {
                dateLocation.style.cssText = 'font-size: 12.5px !important;';
            }
            
            const directorSignature = elem.querySelector('.director-signature');
            if (directorSignature) {
                directorSignature.style.cssText = 'text-align: left !important; margin-top: 20px !important;';
            }
            
            const signatures = elem.querySelector('.signatures');
            if (signatures) {
                signatures.style.cssText = 'display: flex !important; align-items: flex-start !important; gap: 20px !important; margin-top: 20px !important;';
            }
            
            const signatureText = elem.querySelector('.signature-text');
            if (signatureText) {
                signatureText.style.cssText = 'display: flex !important; flex-direction: column !important; justify-content: flex-start !important;';
            }
            
            const signatureImages = elem.querySelector('.signature-images');
            if (signatureImages) {
                signatureImages.style.cssText = 'display: flex !important; align-items: flex-start !important; gap: 15px !important;';
            }
            
            // Director title and name
            const directorTitle = elem.querySelector('.director-title');
            const directorName = elem.querySelector('.director-name');
            if (directorTitle) directorTitle.style.cssText = 'font-weight: bold !important; margin-bottom: 10px !important; font-size: 12.5px !important;';
            if (directorName) directorName.style.cssText = 'font-weight: bold !important; margin-bottom: 20px !important; font-size: 12.5px !important;';
            
            // Signature and stamp images
            const signatureImg = elem.querySelector('.signature-img');
            const stampImg = elem.querySelector('.stamp-img');
            if (signatureImg) signatureImg.style.cssText = 'max-width: 90px !important; height: auto !important;';
            if (stampImg) stampImg.style.cssText = 'max-width: 90px !important; height: auto !important;';
            
            // QR Code container
            const qrContainer = elem.querySelector('.qr-container');
            if (qrContainer) {
                qrContainer.style.cssText = 'position: absolute !important; bottom: 2cm !important; right: 2cm !important; text-align: center !important; background-color: white !important; padding: 10px !important; border: 1px solid #ddd !important; border-radius: 8px !important; box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;';
            }
            
            const qrCode = elem.querySelector('.qr-code');
            if (qrCode) {
                qrCode.style.cssText = 'width: 100px !important; height: 100px !important; border: 2px solid #000 !important; border-radius: 4px !important;';
            }
        };
        
        applyInlineStyles(clone);
        
        // Create a temporary container
        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = 'position: fixed; top: -9999px; left: -9999px; z-index: -1; overflow: hidden;';
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        // Force single-page (A4) PDF by scaling the content to fit inside one page
        const A4_WIDTH_PX = 210 / 25.4 * 96;   // ≈ 794px at 96 DPI
        const A4_HEIGHT_PX = 297 / 25.4 * 96;  // ≈ 1123px at 96 DPI

        // Wrap clone in fixed A4 sized wrapper
        const wrapper = document.createElement('div');
        wrapper.style.width = `${A4_WIDTH_PX}px`;
        wrapper.style.height = `${A4_HEIGHT_PX}px`;
        wrapper.style.margin = '0';
        wrapper.style.padding = '0';
        wrapper.style.overflow = 'hidden';
        wrapper.style.background = 'white';
        wrapper.appendChild(clone);
        tempContainer.appendChild(wrapper);

        // Compute scale so whole content fits one page
        const contentWidth = clone.scrollWidth;
        const contentHeight = clone.scrollHeight;
        const scale = Math.min(A4_WIDTH_PX / contentWidth, A4_HEIGHT_PX / contentHeight);

        clone.style.transformOrigin = 'top left';
        clone.style.transform = `scale(${scale})`;
        clone.style.position = 'relative';

        const options = {
            margin: 0,
            filename: `${attestation.noms.replace(/\s+/g, '_')}_attestation.pdf`,
            html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: 'white'
            },
            jsPDF: { format: 'a4', orientation: 'portrait', unit: 'pt' },
            pagebreak: { mode: ['avoid-all'] }
        };

        html2pdf()
            .set(options)
            .from(wrapper)
            .toPdf()
            .get('pdf')
            .then(pdf => {
            // Safety: remove extra pages if any were created
            while (pdf.internal.getNumberOfPages() > 1) {
                pdf.deletePage(pdf.internal.getNumberOfPages());
            }
            })
            .save()
            .then(() => {
            document.body.removeChild(tempContainer);
            });
    };



    return (
        <div className="UNCR">
            <div className="attestation-form-container">
                <div style={{ textAlign: 'center' }}>
                    <img src={logo} alt="Logo UNCR" className="attestation-form-logo" />
                    <h2>Formulaire d'Attestation d'Inscription Doctorale</h2>
                </div>
                <form onSubmit={handleSubmit} className="attestation-form">
                    <div className="attestation-form-group">
                        <select
                            id="language"
                            name="language"
                            value={formData.language}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="Français">Français</option>
                            <option value="English">English</option>
                        </select>
                    </div>

                    <div className="attestation-form-group">
                        <select
                            id="genre"
                            name="genre"
                            value={formData.genre}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="Femme">{formData.language === 'English' ? 'Female' : 'Femme'}</option>
                            <option value="Homme">{formData.language === 'English' ? 'Male' : 'Homme'}</option>
                        </select>
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="noms"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' 
                                ? `Name of the Doctoral Student`
                                : `Noms d${formData.genre === 'Femme' ? 'e la Doctorante' : 'u Doctorant'}`}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="matricule"
                            name="matricule"
                            value={formData.matricule}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Registration Number' : 'Matricule'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="filiere"
                            name="filiere"
                            value={formData.filiere}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Field' : 'Filière'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="departement"
                            name="departement"
                            value={formData.departement}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Department' : 'Département'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="classe"
                            name="classe"
                            value={formData.classe}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Class' : 'Classe'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="anneeAcademique"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Academic Year (Ex: 2024-2025)' : 'Année Académique (Ex: 2024-2025)'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="laboratoire"
                            name="laboratoire"
                            value={formData.laboratoire}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Affiliated Laboratory' : 'Laboratoire de rattachement'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="text"
                            id="universiteCodeplomation"
                            name="universiteCodeplomation"
                            value={formData.universiteCodeplomation}
                            onChange={handleInputChange}
                            placeholder={formData.language === 'English' ? 'Co-degree University' : 'Université de codiplomation'}
                            required
                        />
                    </div>

                    <div className="attestation-form-group">
                        <input
                            type="date"
                            id="dateEmission"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="attestation-form-group full-width">
                        <button type="submit" className="attestation-generate-btn">
                            Créer l'Attestation
                        </button>
                    </div>
                </form>

                {/* Search Bar */}
                <div className="attestation-search-bar" style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    width: '90%',
                    marginTop: '10px',
                    marginBottom: '20px',
                    alignItems: 'center',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6'
                }}>
                    <input
                        type="text"
                        placeholder="Rechercher par nom ou matricule..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            flex: 1,
                            padding: '10px 15px',
                            fontSize: '14px',
                            border: '1px solid #ced4da',
                            borderRadius: '6px',
                            outline: 'none',
                            transition: 'border-color 0.3s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#28a745'}
                        onBlur={(e) => e.target.style.borderColor = '#ced4da'}
                    />
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{
                            padding: '10px 15px',
                            fontSize: '14px',
                            border: '1px solid #ced4da',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            outline: 'none',
                            minWidth: '150px'
                        }}
                    >
                        <option value="date">Trier par date</option>
                        <option value="name">Trier par nom</option>
                    </select>
                </div>

                {/* List of saved attestations */}
                <div className="attestation-list">
                    {attestations
                        .filter(attestation => 
                            attestation.noms?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            attestation.matricule?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .sort((a, b) => {
                            if (sortBy === 'date') {
                                const dateA = new Date(a.createdAt || 0);
                                const dateB = new Date(b.createdAt || 0);
                                return dateB - dateA;
                            } else {
                                return (a.noms || '').localeCompare(b.noms || '');
                            }
                        })
                        .map((attestation) => (
                        <div key={attestation.id} className="attestation-item">
                            <button
                                className="attestation-toggle"
                                onClick={() => showAttestationModal(attestation)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {attestation.noms} - {attestation.matricule}
                            </button>
                            <button
                                className="delete-btn"
                                onClick={() => handleDeleteAttestation(attestation.id)}
                            >
                                <Delete size={15} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Modal for attestation preview - Scoped Ant Design styles */}
                <ConfigProvider
                    theme={{
                        token: {
                            // Prevent Ant Design from affecting global styles
                        }
                    }}
                >
                    <Modal
                        title={
                            <span style={{ color: 'white' }}>
                                {selectedAttestation 
                                    ? (displayLanguage === 'Français' 
                                        ? `Aperçu - ${selectedAttestation.noms}` 
                                        : `Preview - ${selectedAttestation.noms}`)
                                    : (displayLanguage === 'Français' 
                                        ? 'Aperçu de l\'Attestation' 
                                        : 'Attestation Preview')}
                            </span>
                        }
                        open={modalVisible}
                        onCancel={handleModalClose}
                        width="95vw"
                        style={{ top: 10 }}
                        className="attestation-modal"
                        centered={false}
                        maskClosable={true}
                        keyboard={true}
                        footer={[
                            <Button
                                key="translate"
                                icon={<Globe size={16} />}
                                onClick={toggleDisplayLanguage}
                                style={{
                                    backgroundColor: '#17a2b8',
                                    borderColor: '#17a2b8',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            >
                                {displayLanguage === 'Français' ? 'View in English' : 'Voir en Français'}
                            </Button>,
                            <Button
                                key="download"
                                type="primary"
                                icon={<Download size={16} />}
                                onClick={() => selectedAttestation && downloadPDF(selectedAttestation)}
                                style={{
                                    backgroundColor: '#28a745',
                                    borderColor: '#28a745',
                                    fontWeight: 'bold'
                                }}
                            >
                                {displayLanguage === 'Français' ? 'Télécharger PDF' : 'Download PDF'}
                            </Button>,
                            <Button
                                key="close"
                                onClick={handleModalClose}
                                style={{ fontWeight: 'bold' }}
                            >
                                {displayLanguage === 'Français' ? 'Fermer' : 'Close'}
                            </Button>
                        ]}
                    >
                        {selectedAttestation && (
                            <div className="attestation-modal-content">
                                <AttestationTemplate formData={selectedAttestation} displayLanguage={displayLanguage} />
                            </div>
                        )}
                    </Modal>
                </ConfigProvider>
            </div>
        </div>
    );
};

const AttestationTemplate = ({ formData, displayLanguage }) => {
    const qrCodeRef = useRef();
    // Use displayLanguage if provided (for viewing), otherwise use the document's original language
    const language = displayLanguage || formData.language || 'Français';

    // Helper to format date with month in letters (French or English)
    const formatDateWithMonthName = (dateStr, lang = language) => {
        const date = new Date(dateStr);
        const monthsFr = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const monthsEn = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const months = lang === 'English' ? monthsEn : monthsFr;
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    // Generate QR Code with form data
    useEffect(() => {
        const generateQRCode = async () => {
            const currentLang = displayLanguage || language;
            const isEnglish = currentLang === 'English';
            const qrData = isEnglish
                ? `ADMISSION LETTER - UNCR
            Name: ${formData.noms}
            Registration Number: ${formData.matricule}
            Field: ${formData.filiere}
            Department: ${formData.departement}
            Class: ${formData.classe}
            Academic Year: ${formData.anneeAcademique}
            Laboratory: ${formData.laboratoire}
            Co-degree University: ${formData.universiteCodeplomation}
            Issue Date: ${formatDateWithMonthName(formData.dateEmission, 'English')}
            Status: Verified Attestation
            Issued by: UNCR - Academy of the Circle of Reflection of Nations`
                : `ATTESTATION D'INSCRIPTION DOCTORALE - UNCR
            Noms: ${formData.noms}
            Genre: ${formData.genre}
            Matricule: ${formData.matricule}
            Filière: ${formData.filiere}
            Département: ${formData.departement}
            Classe: ${formData.classe}
            Année Académique: ${formData.anneeAcademique}
            Laboratoire: ${formData.laboratoire}
            Université de codiplomation: ${formData.universiteCodeplomation}
            Date d'émission: ${formatDateWithMonthName(formData.dateEmission, 'Français')}
            Statut: Attestation Vérifiée
            Émis par: UNCR - Académie du Cercle de Réflexion des Nations`;
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF'
                    }
                });
                if (qrCodeRef.current) {
                    qrCodeRef.current.src = qrCodeUrl;
                }
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };

        if (formData) {
            generateQRCode();
        }
    }, [formData, language, displayLanguage]);

    // Helper to get correct text based on genre and language
    const getDoctorantText = () => {
        if (language === 'English') {
            return "the Doctoral Student";
        }
        return formData.genre === 'Femme' ? "la Doctorante" : "le Doctorant";
    };

    const getChercheurText = () => {
        if (language === 'English') {
            return "the Researcher";
        }
        return formData.genre === 'Femme' ? "la Chercheuse" : "le Chercheur";
    };

    const getEnregistreText = () => {
        if (language === 'English') {
            return formData.genre === 'Femme' ? "registered" : "registered";
        }
        return formData.genre === 'Femme' ? "enregistrée" : "enregistré";
    };

    return (
        <div className='UNCR'>
            <div className="attestation-container">
                {language === 'English' ? (
                    <AttestationEnglish formData={formData} qrCodeRef={qrCodeRef} formatDateWithMonthName={formatDateWithMonthName} />
                ) : (
                    <AttestationFrench formData={formData} qrCodeRef={qrCodeRef} formatDateWithMonthName={formatDateWithMonthName} getChercheurText={getChercheurText} getEnregistreText={getEnregistreText} />
                )}
            </div>
        </div>
    );
};

const AttestationFrench = ({ formData, qrCodeRef, formatDateWithMonthName, getChercheurText, getEnregistreText }) => {
    return (
        <div className="attestation-document" id={`attestation-${formData.matricule}`}>
            <style>
                {`
                .attestation-document {
                    width: 19cm !important;
                    min-height: 20.7cm !important;
                    margin: 0 auto !important;
                    padding: 2cm !important;
                    background-color: white !important;
                    font-family: 'Times New Roman', serif !important;
                    line-height: 1.4 !important;
                    position: relative !important;
                    box-sizing: border-box !important;
                    overflow: hidden !important;
                }
                .attestation-header {
                    text-align: center !important;
                }
                .academy-name {
                    color: #28a745 !important;
                    font-size: 20px !important;
                    font-weight: bold !important;
                    margin: 0 0 10px 0 !important;
                }
                .university-name {
                    color: #28a745 !important;
                    font-size: 18px !important;
                    font-weight: bold !important;
                    margin: 10px 0 !important;
                }
                .address, .contact {
                    font-size: 12.5px !important;
                    color: #333 !important;
                    margin: 5px 0 !important;
                }
                .separator {
                    height: 2px !important;
                    background-color: #000 !important;
                    margin: 20px 0 !important;
                    border: none !important;
                }
                .title {
                    text-align: center !important;
                    font-size: 18px !important;
                    font-weight: bold !important;
                    color: #000 !important;
                    margin: 20px 0 !important;
                    text-transform: uppercase !important;
                }
                .academic-year {
                    text-align: center !important;
                    font-size: 16px !important;
                    font-weight: bold !important;
                }
                .document-body {
                    font-size: 12.5px !important;
                    line-height: 1.8 !important;
                }
                .introduction, .registration-text {
                    margin: 20px 0 !important;
                    text-align: justify !important;
                    font-size: 12.5px !important;
                }
                .student-details {
                    margin: 20px 0 !important;
                }
                .student-details p {
                    margin: 8px 0 !important;
                    font-size: 12.5px !important;
                }
                .signature-section {
                    margin-top: 40px !important;
                }
                .date-location {
                    font-size: 12.5px !important;
                }
                .director-signature {
                    text-align: left !important;
                    margin-top: 40px !important;
                }
                .signatures {
                    display: flex !important;
                    align-items: flex-start !important;
                    gap: 20px !important;
                    margin-top: 20px !important;
                }
                .signature-text {
                    display: flex !important;
                    flex-direction: column !important;
                    justify-content: flex-start !important;
                }
                .signature-images {
                    display: flex !important;
                    align-items: flex-start !important;
                    gap: 15px !important;
                }
                .director-title, .director-name {
                    font-weight: bold !important;
                    font-size: 12.5px !important;
                    margin-bottom: 10px !important;
                }
                .signature-img, .stamp-img {
                    max-width: 90px !important;
                    height: auto !important;
                }
                .qr-container {
                    position: absolute !important;
                    bottom: 2cm !important;
                    right: 2cm !important;
                    text-align: center !important;
                    background-color: white !important;
                    padding: 10px !important;
                    border: 1px solid #ddd !important;
                    border-radius: 8px !important;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                }
                .qr-code {
                    width: 100px !important;
                    height: 100px !important;
                    border: 2px solid #000 !important;
                    border-radius: 4px !important;
                }
                `}
            </style>
            <div className="attestation-header">
                <div className="header-text">
                    <h2 className="academy-name">Académie du Cercle de Réflexion des Nations</h2>
                    <div className="logo-container">
                        <img src={logo} alt="Logo UNCR" className="logo" />
                    </div>
                    <h3 className="university-name">UNCR Université en cotutelle internationale de thèse</h3>
                    <p className="address"><strong>1522 Cleveland Street, Hollywood 33020, Florida, USA</strong></p>
                    <p className="contact"><strong>Web : crn-nations.org; E-mail : contact@crn-nations.org</strong></p>
                </div>
            </div>

            <div className="separator"></div>

            <div className="attestation-content">
                <h1 className="title">ATTESTATION D'INSCRIPTION EN FORMATION DOCTORALE</h1>
                <p className="academic-year">Année Académique: {formData.anneeAcademique}</p>

                <div className="document-body">
                    <p className="introduction">
                        Le Directeur Général de l'Académie du Cercle de Réflexion des Nations aux États Unis atteste par la présente que {getChercheurText()} dont les détails suivants :
                    </p>

                    <div className="student-details">
                        <p>Noms d{formData.genre === 'Femme' ? 'e la Doctorante' : 'u Doctorant'}: <strong>{formData.noms}</strong></p>
                        <p>Matricule: <strong>{formData.matricule}</strong></p>
                        <p>Filière: <strong>{formData.filiere}</strong></p>
                        <p>Département: <strong>{formData.departement}</strong></p>
                        <p>Classe: <strong>{formData.classe}</strong></p>
                        <p>Laboratoire de rattachement : <strong>CIREP/Unité de recherche : {formData.laboratoire}</strong></p>
                    </div>

                    <p className="registration-text">
                        Est dûment {getEnregistreText()} en formation doctorale en cotutelle internationale de thèse et codiplomation entre UNCR University et {formData.universiteCodeplomation}
                    </p>

                    <div className="signature-section">
                        <p className="date-location">Établi à Hollywood le {formatDateWithMonthName(formData.dateEmission, 'Français')}</p>
                        <div className="director-signature">
                            <div className="signatures">
                                <div className="signature-text">
                                    <p className="director-title">Le Directeur Général</p>
                                    <p className="director-name">Pr NIZEYIMANA Jean Baptiste</p>
                                </div>
                                <div className="signature-images">
                                    <img src="/bap_sig.png" alt="Signature" className="signature-img" />
                                    <img src="/Bap_Cache.png" alt="Cachet" className="stamp-img" />
                                </div>
                                <div className="qr-container">
                                    <img
                                        ref={qrCodeRef}
                                        alt="QR Code - Vérification Attestation"
                                        className="qr-code"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

const AttestationEnglish = ({ formData, qrCodeRef, formatDateWithMonthName }) => {
    return (
        <div className="attestation-document" id={`attestation-${formData.matricule}`}>
            <style>
                {`
                .attestation-document {
                    width: 19cm !important;
                    min-height: 20.7cm !important;
                    margin: 0 auto !important;
                    padding: 2cm !important;
                    background-color: white !important;
                    font-family: 'Times New Roman', serif !important;
                    line-height: 1.4 !important;
                    position: relative !important;
                    box-sizing: border-box !important;
                    overflow: hidden !important;
                }
                .attestation-header {
                    text-align: center !important;
                }
                .academy-name {
                    color: #28a745 !important;
                    font-size: 20px !important;
                    font-weight: bold !important;
                    margin: 0 0 10px 0 !important;
                }
                .university-name {
                    color: #28a745 !important;
                    font-size: 18px !important;
                    font-weight: bold !important;
                    margin: 10px 0 !important;
                }
                .address, .contact {
                    font-size: 12.5px !important;
                    color: #333 !important;
                    margin: 5px 0 !important;
                }
                .separator {
                    height: 2px !important;
                    background-color: #000 !important;
                    margin: 20px 0 !important;
                    border: none !important;
                }
                .title {
                    text-align: center !important;
                    font-size: 18px !important;
                    font-weight: bold !important;
                    color: #000 !important;
                    margin: 20px 0 !important;
                    text-transform: uppercase !important;
                }
                .academic-year {
                    text-align: center !important;
                    font-size: 16px !important;
                    font-weight: bold !important;
                }
                .document-body {
                    font-size: 12.5px !important;
                    line-height: 1.8 !important;
                }
                .introduction, .registration-text {
                    margin: 20px 0 !important;
                    text-align: justify !important;
                    font-size: 12.5px !important;
                }
                .student-details {
                    margin: 20px 0 !important;
                }
                .student-details p {
                    margin: 8px 0 !important;
                    font-size: 12.5px !important;
                }
                .signature-section {
                    margin-top: 40px !important;
                }
                .date-location {
                    font-size: 12.5px !important;
                }
                .director-signature {
                    text-align: left !important;
                    margin-top: 40px !important;
                }
                .signatures {
                    display: flex !important;
                    align-items: flex-start !important;
                    gap: 20px !important;
                    margin-top: 20px !important;
                }
                .signature-text {
                    display: flex !important;
                    flex-direction: column !important;
                    justify-content: flex-start !important;
                }
                .signature-images {
                    display: flex !important;
                    align-items: flex-start !important;
                    gap: 15px !important;
                }
                .director-title, .director-name {
                    font-weight: bold !important;
                    font-size: 12.5px !important;
                    margin-bottom: 10px !important;
                }
                .signature-img, .stamp-img {
                    max-width: 90px !important;
                    height: auto !important;
                }
                .qr-container {
                    position: absolute !important;
                    bottom: 2cm !important;
                    right: 2cm !important;
                    text-align: center !important;
                    background-color: white !important;
                    padding: 10px !important;
                    border: 1px solid #ddd !important;
                    border-radius: 8px !important;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                }
                .qr-code {
                    width: 100px !important;
                    height: 100px !important;
                    border: 2px solid #000 !important;
                    border-radius: 4px !important;
                }
                `}
            </style>
            <div className="attestation-header">
                <div className="header-text">
                    <h2 className="academy-name">Académie du Cercle de Réflexion des Nations</h2>
                    <div className="logo-container">
                        <img src={logo} alt="Logo UNCR" className="logo" />
                    </div>
                    <h3 className="university-name">UNCR Université en cotutelle internationale de thèse</h3>
                    <p className="address"><strong>1522 Cleveland Street, Hollywood 33020, Florida, USA</strong></p>
                    <p className="contact"><strong>Web : crn-nations.org; E-mail : contact@crn-nations.org</strong></p>
                </div>
            </div>

            <div className="separator"></div>

            <div className="attestation-content">
                <h1 className="title">ADMISSION LETTER</h1>
                <p className="academic-year">Academic Year: {formData.anneeAcademique}</p>

                <div className="document-body">
                    <p className="introduction">
                        The Director General of the Academy of the Circle of Reflection of Nations in the United States hereby certifies that the Researcher with the following details:
                    </p>

                    <div className="student-details">
                        <p>Name of the Doctoral Student: <strong>{formData.noms}</strong></p>
                        <p>Registration Number: <strong>{formData.matricule}</strong></p>
                        <p>Field: <strong>{formData.filiere}</strong></p>
                        <p>Department: <strong>{formData.departement}</strong></p>
                        <p>Class: <strong>{formData.classe}</strong></p>
                        <p>Affiliated Laboratory: <strong>CIREP/Research Unit: {formData.laboratoire}</strong></p>
                    </div>

                    <p className="registration-text">
                        Is duly registered in doctoral training in international joint thesis supervision and co-degree between UNCR University and {formData.universiteCodeplomation}
                    </p>

                    <div className="signature-section">
                        <p className="date-location">Issued in Hollywood on {formatDateWithMonthName(formData.dateEmission, 'English')}</p>
                        <div className="director-signature">
                            <div className="signatures">
                                <div className="signature-text">
                                    <p className="director-title">The Director General</p>
                                    <p className="director-name">Pr NIZEYIMANA Jean Baptiste</p>
                                </div>
                                <div className="signature-images">
                                    <img src="/bap_sig.png" alt="Signature" className="signature-img" />
                                    <img src="/Bap_Cache.png" alt="Stamp" className="stamp-img" />
                                </div>
                                <div className="qr-container">
                                    <img
                                        ref={qrCodeRef}
                                        alt="QR Code - Attestation Verification"
                                        className="qr-code"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Attestation;
